import { Organisation } from '@futrli-sdk/organisations';
import { WorkspaceMember } from '@futrli-sdk/people';

import { getUserName } from '../../utils/users/getUserName';
import { USER_ROLE_IDS } from './constants';
import { WorkspaceMemberOrganisationPatch, WorkspaceMemberPatch } from '../../../predict/src/core/types';

export interface UserFormValues {
  email: string;
  fullName: string;
  roleId: string;
  selectedOrganisations: Record<string, boolean>;
}

export interface ModifyUserChanges {
  addedOrganisations: WorkspaceMemberOrganisationPatch[];
  removedOrganisationIds: string[];
  roleId: string;
}

const EXCLUDED_ORGANISATION_KINDS: Organisation['kind'][] = ['sage_non_practice', 'sage_practice'];

/**
 * Returns the organisations that can be explicitly assigned to a workspace member.
 */
export const getEligibleUserOrganisations = (organisations: Organisation[]): Organisation[] =>
  organisations.filter(organisation => !EXCLUDED_ORGANISATION_KINDS.includes(organisation.kind));

/**
 * Converts user form values into the API payload used to invite a workspace member.
 */
export const getInviteUserPayload = (
  values: UserFormValues,
  organisations: Organisation[]
): WorkspaceMemberPatch => {
  const [firstName, ...lastName] = values.fullName.trim().split(/\s+/);
  const selectedOrganisations = organisations
    .filter(organisation => values.selectedOrganisations[organisation.id])
    .map(organisation => ({
      datasources: [{ integration_id: organisation.datasource.integration_id }],
      id: organisation.id
    }));

  return {
    email_address: values.email.trim(),
    name: {
      first_name: firstName,
      last_name: lastName.join(' ')
    },
    organisations: values.roleId === USER_ROLE_IDS.SUPER_ADMIN ? [] : selectedOrganisations,
    role: {
      id: values.roleId
    }
  };
};

/**
 * Converts an existing workspace member into pre-filled user form values.
 */
export const getModifyUserInitialValues = (user: WorkspaceMember): UserFormValues => ({
  email: user.email_address,
  fullName: getUserName(user.name),
  roleId: user.role.id,
  selectedOrganisations: user.organisations.reduce<Record<string, boolean>>((selection, organisation) => {
    selection[organisation.id] = true;
    return selection;
  }, {})
});

/**
 * Calculates the role and organisation mutations required to apply edited user form values.
 */
export const getModifyUserChanges = (
  values: UserFormValues,
  user: WorkspaceMember,
  organisations: Organisation[]
): ModifyUserChanges => {
  const currentOrganisationIds = new Set(user.organisations.map(organisation => organisation.id));

  return {
    addedOrganisations: organisations
      .filter(
        organisation => values.selectedOrganisations[organisation.id] && !currentOrganisationIds.has(organisation.id)
      )
      .map(organisation => ({
        datasources: [{ integration_id: organisation.datasource.integration_id }],
        id: organisation.id
      })),
    removedOrganisationIds: organisations
      .filter(
        organisation => currentOrganisationIds.has(organisation.id) && !values.selectedOrganisations[organisation.id]
      )
      .map(organisation => organisation.id),
    roleId: values.roleId
  };
};
